import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import Header from '../../components/common/global-top-bar';
import { filterIcon } from '../../assets/images';
import Spinner from '../../components/common/spinner';
import moment from 'moment';
import { ROUTE_PATH } from '../../routes/paths';
import { convertObjectToParams } from '../../components/common/create-params';
import { ListenerRole, PeerOptions, PeerType, usePeers } from '../../hooks/usePeers';
import { Tier, useTiers } from '../../hooks/useTiers';
import { EXPERIENCE_GRADE } from '../../hooks/useListenerAudio';
import { useProfileTagGroups } from '../../hooks/useProfileTagGroups';
import { use2026TagGroups } from '../../hooks/useTagGroups';
import FilterBar from './components/filter-bar';
import FilterModal from './components/filter-modal';
import SwitchBar from '../../components/switch-bar';
import { Avatar } from '../../components/ui';
import { PEER_NAVIGATION } from './peer-constants';
import VisibilityIcon from '../../components/peer-listing/visibility-icon';
import ReviewStatusIcon from '../../components/peer-listing/review-status-icon';
import UserTypeIcon from '../../components/peer-listing/user-type-icon';
import { usePeerListingUrlState } from './hooks/usePeerListingUrlState';
import { deriveContentReviewTypes, isAllContentReviewTypesSelected } from './utils/contentReview';
import { isAvatarPendingAdminReview } from '../../utils/profilePhoto';
import { useClientMinimalList, useDomains } from '../../hooks/useClients';
import { useTags } from '../../hooks/useTags';

const PeersList: React.FC = (): JSX.Element => {
  const location = useLocation();
  const { data: tiers } = useTiers();
  const { filters, setFilters } = usePeerListingUrlState();

  const { limit, page, userType, isPeerActive, ...rest } = filters;
  const { data: peers, isLoading } = usePeers({
    limit,
    page,
    userType,
    isPeerActive,
    ...rest,
  });

  const [isOpen, setIsOpen] = useState(false);

  const getCurrentTabPosition = () => {
    const currentPath = location.pathname;
    const index = PEER_NAVIGATION.findIndex((nav) => nav.path === currentPath);
    return index >= 0 ? index : 0;
  };

  return (
    <div>
      <Header heading="Peers" />
      <SwitchBar heading={PEER_NAVIGATION} position={getCurrentTabPosition()} />
      <FilterBar count={peers?.count} setFilters={setFilters} filters={filters} />
      {isLoading && <Spinner alignmentClass={'left-64 top-30 fixed z-100 inset-0 overflow-y-auto'} />}
      <div className="max-window-height-connections font-['Montserrat' overflow-y-auto px-7">
        <PeerListingHeader filters={filters} setFilters={setFilters} setIsOpen={setIsOpen} count={peers?.count} />
        <PeerListingBody peers={peers?.data} tiers={tiers} />
        <FilterModal filters={filters} setFilters={setFilters} isOpen={isOpen} setIsOpen={setIsOpen} />
      </div>
    </div>
  );
};

const PeerListingHeader: React.FC<{
  filters?: PeerOptions;
  setFilters: React.Dispatch<React.SetStateAction<PeerOptions>>;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  count?: number;
}> = ({ filters, setFilters, setIsOpen, count }) => {
  const formatDateTimePill = (value: string) => {
    try {
      // input is from <input type="datetime-local"> (no timezone); treat as local for display
      // moment is already used in this file
      return moment(value).format('M/D/YYYY h:mm A');
    } catch {
      return value;
    }
  };

  const getActiveFilters = (filters: PeerOptions) => {
    const activeFilters: { key: string; value: string | number | boolean }[] = [];
    const createdAfter = filters.contentCreatedAfter;
    const createdBefore = filters.contentCreatedBefore;
    const gradedAfter = filters.gradedAfter;
    const gradedBefore = filters.gradedBefore;
    const contentReviewTypes = deriveContentReviewTypes(filters);
    const showMasterReviewPill = contentReviewTypes.length > 0 && isAllContentReviewTypesSelected(filters);

    Object.entries(filters).forEach(([key, value]) => {
      if (['page', 'limit'].includes(key)) {
        return;
      }
      // Content-review pills are rendered from derived review types.
      if (
        [
          'needsAnyContentReview',
          'profilePhotoApproved',
          'displayNameStatus',
          'aboutMeStatus',
          'backgroundTagApproved',
          'needsExperienceApproval',
          'needsExperienceApprovalMediaTypes',
        ].includes(key)
      )
        return;
      // Combine each date range into a single pill (Created… and Graded…).
      if (
        key === 'contentCreatedAfter' ||
        key === 'contentCreatedBefore' ||
        key === 'gradedAfter' ||
        key === 'gradedBefore'
      ) {
        return;
      }
      if (key === 'testUsers') {
        return;
      }
      if (Array.isArray(value) && value.length > 0) {
        value.forEach((filterValue) => {
          activeFilters.push({ key, value: filterValue });
        });
      } else if (typeof value === 'boolean') {
        activeFilters.push({ key, value });
      } else if (value && !Array.isArray(value)) {
        activeFilters.push({ key, value });
      }
    });

    const testUsersValues = filters.testUsers ?? [];
    if (testUsersValues.length === 0) {
      activeFilters.push({ key: 'testUsersAll', value: 'Test and Non-test Peers' });
    } else {
      testUsersValues.forEach((v) => activeFilters.push({ key: 'testUsers', value: v }));
    }

    if (showMasterReviewPill) {
      activeFilters.push({
        key: 'contentReviewMaster',
        value: 'Needs Any Content Reviewed',
      });
    } else if (contentReviewTypes.length > 0) {
      contentReviewTypes.forEach((t: string) => activeFilters.push({ key: 'contentReviewType', value: t }));
    }

    if (createdAfter || createdBefore) {
      if (createdAfter && createdBefore) {
        activeFilters.push({
          key: 'contentCreatedBetween',
          value: `Created Between: ${formatDateTimePill(createdAfter)} - ${formatDateTimePill(createdBefore)}`,
        });
      } else if (createdAfter) {
        activeFilters.push({
          key: 'contentCreatedBetween',
          value: `Created After: ${formatDateTimePill(createdAfter)}`,
        });
      } else if (createdBefore) {
        activeFilters.push({
          key: 'contentCreatedBetween',
          value: `Created Before: ${formatDateTimePill(createdBefore)}`,
        });
      }
    }
    if (gradedAfter || gradedBefore) {
      if (gradedAfter && gradedBefore) {
        activeFilters.push({
          key: 'gradedBetween',
          value: `Graded Between: ${formatDateTimePill(gradedAfter)} - ${formatDateTimePill(gradedBefore)}`,
        });
      } else if (gradedAfter) {
        activeFilters.push({ key: 'gradedBetween', value: `Graded After: ${formatDateTimePill(gradedAfter)}` });
      } else if (gradedBefore) {
        activeFilters.push({ key: 'gradedBetween', value: `Graded Before: ${formatDateTimePill(gradedBefore)}` });
      }
    }

    return activeFilters;
  };

  const activeFilters = getActiveFilters(filters ? filters : {});

  const clearFilter = (filterKey: string, filterValue: string | number | boolean) => {
    setFilters((prevFilters) => {
      const updatedFilters: PeerOptions = { ...prevFilters };
      if (filterKey === 'hideIncompletePeers') {
        updatedFilters.hideIncompletePeers = filterValue === true ? false : true;
        return updatedFilters;
      }
      if (filterKey === 'testUsersAll') {
        updatedFilters.testUsers = ['false'];
        return updatedFilters;
      }
      if (filterKey === 'contentCreatedBetween') {
        delete updatedFilters.contentCreatedAfter;
        delete updatedFilters.contentCreatedBefore;
        return updatedFilters;
      }
      if (filterKey === 'gradedBetween') {
        delete updatedFilters.gradedAfter;
        delete updatedFilters.gradedBefore;
        return updatedFilters;
      }
      if (filterKey === 'contentReviewMaster') {
        delete updatedFilters.needsAnyContentReview;
        delete updatedFilters.profilePhotoApproved;
        delete updatedFilters.displayNameStatus;
        delete updatedFilters.aboutMeStatus;
        delete updatedFilters.backgroundTagApproved;
        delete updatedFilters.needsExperienceApproval;
        delete updatedFilters.needsExperienceApprovalMediaTypes;
        return updatedFilters;
      }
      if (filterKey === 'contentReviewType') {
        const t = String(filterValue).toLowerCase();
        if (t === 'profile_photo') delete updatedFilters.profilePhotoApproved;
        if (t === 'display_name') delete updatedFilters.displayNameStatus;
        if (t === 'about_me') delete updatedFilters.aboutMeStatus;
        if (t === 'background_tags') delete updatedFilters.backgroundTagApproved;
        if (t === 'experience_audio' || t === 'experience_video') {
          const mts = new Set(updatedFilters.needsExperienceApprovalMediaTypes || []);
          if (t === 'experience_audio') mts.delete('audio');
          if (t === 'experience_video') mts.delete('video');
          const nextMts = Array.from(mts) as Array<'audio' | 'video'>;
          updatedFilters.needsExperienceApprovalMediaTypes = nextMts;
          if (nextMts.length === 0) {
            delete updatedFilters.needsExperienceApproval;
            delete updatedFilters.needsExperienceApprovalMediaTypes;
          } else {
            updatedFilters.needsExperienceApproval = true;
          }
        }
        delete updatedFilters.needsAnyContentReview;
        return updatedFilters;
      }
      if (Array.isArray(updatedFilters[filterKey as keyof PeerOptions])) {
        // @ts-ignore
        const next = (updatedFilters[filterKey as keyof PeerOptions] as Array<string | number>).filter(
          (item) => item !== filterValue,
        );
        if (filterKey === 'userType' && next.length === 0) {
          updatedFilters.userType = ['onboardingpeer', 'peer', 'onboardinglistener', 'peerlistener'];
        } else {
          // @ts-ignore
          updatedFilters[filterKey as keyof PeerOptions] = next;
        }
      } else {
        delete updatedFilters[filterKey as keyof PeerOptions];
      }
      return updatedFilters;
    });
  };

  return (
    <div className="mb-4 h-full border-b-4 border-black">
      <div className="flex w-full justify-between pb-2 pt-8 text-left">
        <div className="flex flex-wrap items-start gap-y-2">
          <div className="flex items-center gap-2 pr-4">
            <div className="text-3xl font-bold text-violet-950">Peers</div>
            <button onClick={() => setIsOpen(true)}>
              <img src={filterIcon} className="h-6 w-6" alt="filter" />
            </button>
            <div className="whitespace-nowrap text-xs font-normal text-neutral-600">Filtered by</div>
          </div>
          <ActiveFilters activeFilters={activeFilters} clearFilter={clearFilter} />
        </div>
        <div className="flex items-end space-x-4">
          <div className="pb-1 text-xs font-normal text-neutral-600">Matches</div>
          <div className="font-['Montse text-4xl font-bold text-violet-950">{count}</div>
        </div>
      </div>
    </div>
  );
};

const ActiveFilters: React.FC<{
  activeFilters: {
    key: string;
    value: string | number | boolean;
  }[];
  clearFilter: (filterKey: string, filterValue: string | number | boolean) => void;
}> = ({ activeFilters, clearFilter }) => {
  const { data: filterOptions } = useProfileTagGroups();
  const { data: tiers } = useTiers();
  const { allTags: contentTags } = use2026TagGroups();
  const { data: clients } = useClientMinimalList({ statuses: 'active' });
  const { data: domains } = useDomains();
  const { data: traitTags } = useTags({ include_hidden: true, tag_type: 'trait', limit: 200 });

  return (
    <div className="mx-2 flex flex-wrap gap-2">
      {activeFilters.map(({ key, value }) => {
        let valueString = String(value);
        if (key === 'search') {
          valueString = `Search: ${value}`;
        } else if (key === 'contentTagIds' && typeof value === 'number') {
          valueString = contentTags.find((t) => t.id === value)?.name || String(value);
        } else if (key === 'hasBackgroundTags') {
          valueString = value === 'true' || value === true ? 'Has Bkg Tag' : 'No Bkg Tag';
        } else if (key === 'groupName') {
          valueString = `Org: ${value}`;
        } else if (key === 'clientIds' && typeof value === 'number') {
          valueString = `Client: ${clients?.find((c) => c.id === value)?.name || value}`;
        } else if (key === 'domainId' && typeof value === 'number') {
          valueString = `Domain: ${domains?.find((d) => d.id === value)?.name || value}`;
        } else if (key === 'tagIds' && typeof value === 'number') {
          valueString = traitTags?.find((t) => t.id === value)?.name || `Tag ${value}`;
        } else if (key === 'gradedByUserIds' && typeof value === 'number') {
          valueString = `Graded By: ${value}`;
        } else if (value && typeof value === 'number' && key !== 'tierId') {
          valueString =
            filterOptions
              ?.find((tagGroup) => tagGroup?.tags?.some?.((tag) => tag.id === value))
              ?.tags?.find?.((tag) => tag.id === value)?.name || '';
        } else if (value && key === 'tierId') {
          valueString = tiers?.find((tier) => tier.id === value)?.name || '';
        } else if (key === 'userType') {
          const userTypeLabels: Record<string, string> = {
            onboardingpeer: 'Peer Applicant',
            peer: 'Peer',
            onboardinglistener: 'Listener Applicant',
            peerlistener: 'Listener',
          };
          valueString = userTypeLabels[value as string] || (value as string);
        } else if (key === 'isPeerActive') {
          valueString = value === 'true' ? 'Visible' : 'Hidden';
        } else if (key === 'testUsers') {
          valueString = value === 'true' ? 'Test Peers' : 'Non-test Peers';
        } else if (key === 'hideIncompletePeers') {
          valueString = value === true ? 'Hide Incompletes' : 'Show Incompletes';
        } else if (key === 'contentReviewType') {
          const t = String(value).toLowerCase();
          const labels: Record<string, string> = {
            profile_photo: 'Avatar: Needs Review',
            background_tags: 'Bkg Tags: Needs Review',
            display_name: 'Display Name: Needs Review',
            about_me: 'About Me: Needs Review',
            experience_audio: 'Audio: Needs Review',
            experience_video: 'Video: Needs Review',
          };
          valueString = labels[t] || '';
        }

        if (String(valueString).trim() === '') {
          return null;
        }
        return (
          <div key={`${key}-${value}`} className="flex h-7 items-center justify-between rounded-full bg-gray-200 px-2">
            <div className="truncate p-2 font-['Montserrat'] text-sm font-bold text-zinc-600">{valueString}</div>
            <button
              className="h-4 w-4 items-center rounded-full bg-white text-xs"
              onClick={() => clearFilter(key, value)}
            >
              X
            </button>
          </div>
        );
      })}
    </div>
  );
};

const PEER_STATE_LABELS: Record<string, string> = {
  [PeerType.ACTIVE_LISTENER]: 'Listener',
  [PeerType.ACTIVE_PEER]: 'Peer',
  [PeerType.ONBOARDING_PEER]: 'Peer Applicant',
  [PeerType.UPGRADING_PEER]: 'Listener Applicant',
  [PeerType.REJECTED_PEER]: 'Rejected Peer',
};

const getPeerType = (peer: ListenerRole): 'peer' | 'listener' =>
  peer.state === PeerType.ACTIVE_LISTENER ? 'listener' : 'peer';

const getPeerNavParams = (peer: ListenerRole, peerType: 'peer' | 'listener') =>
  convertObjectToParams({
    listenerRoleId: peer.id,
    listenerId: peer.user.id,
    listenerType: peerType,
    listenerName: peer.user.first_name,
  });

/** Profile fields that appear under "Pending:" on the listing — same rules as content review needs. */
const getProfileContentPendingLabels = (peer: ListenerRole): string[] => {
  const pending: string[] = [];
  if (isAvatarPendingAdminReview(peer)) pending.push('Profile Picture');
  if (peer.proposed_about_me || peer.about_me_rejected) pending.push('About Me');
  if (peer.user.display_name_rejected || peer.user.proposed_display_name) pending.push('Display Name');
  if ((peer.background_tags || []).some((t) => t.status === 'UNGRADED')) pending.push('Background Tags');
  return pending;
};

const PeerListingBody: React.FC<{
  peers: ListenerRole[] | undefined;
  tiers: Tier[] | undefined;
}> = ({ peers, tiers }) => {
  return (
    <div>
      {peers &&
        peers.length > 0 &&
        peers.map((peer: ListenerRole) => <PeerListingRow key={peer.id} peer={peer} tiers={tiers} />)}
    </div>
  );
};

const PeerListingRow: React.FC<{ peer: ListenerRole; tiers: Tier[] | undefined }> = ({ peer, tiers }) => {
  const history = useHistory();
  const peerType = getPeerType(peer);
  const isVisible = peer.is_peer_active;
  const navParams = getPeerNavParams(peer, peerType);
  const profilePath = (peerType === 'peer' ? ROUTE_PATH.PEERS_PROFILE : ROUTE_PATH.LISTENERS_PROFILE) + navParams;
  const contentReviewPath =
    (peerType === 'peer' ? ROUTE_PATH.PEERS_CONTENT_REVIEW : ROUTE_PATH.LISTENERS_CONTENT_REVIEW) + navParams;
  const tier = tiers?.find((t) => t.id === peer.tier_id);

  const experiences = peer.experiences ?? [];
  const audioApproved = experiences.filter(
    (e) => e.media_type === 'AUDIO' && e.message_quality === EXPERIENCE_GRADE.approved,
  ).length;
  const audioUngraded = experiences.filter(
    (e) => e.media_type === 'AUDIO' && e.message_quality === EXPERIENCE_GRADE.ungraded,
  ).length;
  const videoApproved = experiences.filter(
    (e) => e.media_type === 'VIDEO' && e.message_quality === EXPERIENCE_GRADE.approved,
  ).length;
  const videoUngraded = experiences.filter(
    (e) => e.media_type === 'VIDEO' && e.message_quality === EXPERIENCE_GRADE.ungraded,
  ).length;

  const profilePendingLabels = getProfileContentPendingLabels(peer);
  const hasExperiencePending = audioUngraded > 0 || videoUngraded > 0;
  const showReviewContent = profilePendingLabels.length > 0 || hasExperiencePending;

  return (
    <div className="mb-4 rounded border-2 border-gray-300 bg-white">
      <div
        role="button"
        className="text-gray-dark flex w-full p-4 text-left hover:bg-gray-50"
        onClick={() => history.push(profilePath)}
      >
        <div className="flex w-full gap-4">
          <div className="flex-shrink-0">
            {peer.current_profile_photo !== null ? (
              <Avatar variant="rounder" image={peer.current_profile_photo.square_url} className="cursor-pointer" />
            ) : (
              <div className="flex h-24 w-24 items-center justify-center rounded-full bg-gray-400">
                <p className="text-xs">No Avatar</p>
              </div>
            )}
          </div>

          <div className="flex flex-1 gap-6">
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-neutral-600">#{peer.user.id}</span>
                <VisibilityIcon isVisible={isVisible} peerType={peerType} canTakeCalls={peer.is_active} />
                <ReviewStatusIcon
                  profilePhotoApproved={peer.profile_photo_approved}
                  proposedProfilePhoto={isAvatarPendingAdminReview(peer)}
                  displayNameRejected={peer.user.display_name_rejected}
                  proposedDisplayName={peer.user.proposed_display_name}
                  aboutMeRejected={peer.about_me_rejected}
                  proposedAboutMe={peer.proposed_about_me}
                />
                <UserTypeIcon state={peer.state} />
              </div>

              <p className={`text-black ${peer.user.display_name ? 'text-xl font-bold' : 'text-sm italic'}`}>
                {peer.user.display_name ? peer.user.display_name : 'No display name'}
              </p>

              <p className="text-sm text-black">
                {peer.user.first_name || peer.user.last_name
                  ? `${peer.user.first_name} ${peer.user.last_name}`
                  : 'No name'}
              </p>

              <p className="truncate text-xs text-neutral-600">{peer.user.email_address}</p>
            </div>

            <div className="flex w-32 flex-col gap-1 text-xs">
              <p className="text-neutral-600">{tiers?.find((t) => t.id === peer.tier_id)?.name}</p>
              <p className="text-neutral-600">{moment(peer?.user?.created_at).format('MMM D, YYYY')}</p>
              <p className="mt-1 font-bold text-black">{PEER_STATE_LABELS[peer.state] ?? 'Unknown'}</p>
            </div>

            <PeerExperienceInfo
              audioApproved={audioApproved}
              audioUngraded={audioUngraded}
              audioMax={tier?.max_experience_payouts ?? null}
              videoApproved={videoApproved}
              videoUngraded={videoUngraded}
              videoMax={tier?.max_video_experience_payouts ?? null}
              pendingLabels={profilePendingLabels}
            />

            <div className="flex w-40 flex-col gap-1 text-xs">
              {peerType === 'listener' ? (
                <>
                  <p className="text-neutral-600">
                    Successful Calls: <span className="font-bold text-black">{peer.calls_succeeded}</span>
                  </p>
                  <p className="text-neutral-600">
                    Call Requests: <span className="font-bold text-black">{peer.connection_requests}</span>
                  </p>
                </>
              ) : (
                <p className="text-neutral-600">
                  Experience Plays: <span className="font-bold text-black">{peer.num_playbacks}</span>
                </p>
              )}
              <p className="mt-1 text-neutral-600">
                Last Week's Earnings:{' '}
                <span className="font-bold text-black">
                  {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                    (peer?.earnings || 0) / 100,
                  )}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 border-t border-gray-200 bg-gray-50 px-4 py-2">
        <button
          className="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
          onClick={(e) => {
            e.stopPropagation();
            history.push(profilePath);
          }}
        >
          Edit Profile
        </button>
        {showReviewContent && (
          <button
            className="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            onClick={(e) => {
              e.stopPropagation();
              history.push(contentReviewPath);
            }}
          >
            Review Content
          </button>
        )}
      </div>
    </div>
  );
};

const TriColorBar: React.FC<{
  approved: number;
  ungraded: number;
  max: number | null;
}> = ({ approved, ungraded, max }) => {
  const safeMax = max && max > 0 ? max : Math.max(approved + ungraded, 1);
  const approvedPct = Math.min((approved / safeMax) * 100, 100);
  const ungradedPct = Math.min((ungraded / safeMax) * 100, 100 - approvedPct);

  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
      <div className="flex h-full">
        {approvedPct > 0 && <div style={{ width: `${approvedPct}%`, backgroundColor: '#1E40AF' }} />}
        {ungradedPct > 0 && <div style={{ width: `${ungradedPct}%`, backgroundColor: '#60A5FA' }} />}
      </div>
    </div>
  );
};

const PeerExperienceInfo: React.FC<{
  audioApproved: number;
  audioUngraded: number;
  audioMax: number | null;
  videoApproved: number;
  videoUngraded: number;
  videoMax: number | null;
  pendingLabels: string[];
}> = ({ audioApproved, audioUngraded, audioMax, videoApproved, videoUngraded, videoMax, pendingLabels }) => {
  const audioTotal = audioApproved + audioUngraded;
  const videoTotal = videoApproved + videoUngraded;

  return (
    <div className="flex w-60 flex-col gap-2 text-xs">
      <div>
        <div className="mb-0.5 flex justify-between text-neutral-600">
          <span>Audio Experiences</span>
          <span className="font-semibold text-black">
            {audioTotal}
            {audioMax !== null ? `/${audioMax}` : ''}
          </span>
        </div>
        <TriColorBar approved={audioApproved} ungraded={audioUngraded} max={audioMax} />
        <div className="mt-0.5 flex gap-3 text-neutral-500" style={{ fontSize: '10px' }}>
          <span style={{ color: '#1E40AF' }}>● {audioApproved} approved</span>
          <span style={{ color: '#60A5FA' }}>● {audioUngraded} pending</span>
        </div>
      </div>

      <div>
        <div className="mb-0.5 flex justify-between text-neutral-600">
          <span>Video Experiences</span>
          <span className="font-semibold text-black">
            {videoTotal}
            {videoMax !== null ? `/${videoMax}` : ''}
          </span>
        </div>
        <TriColorBar approved={videoApproved} ungraded={videoUngraded} max={videoMax} />
        <div className="mt-0.5 flex gap-3 text-neutral-500" style={{ fontSize: '10px' }}>
          <span style={{ color: '#1E40AF' }}>● {videoApproved} approved</span>
          <span style={{ color: '#60A5FA' }}>● {videoUngraded} pending</span>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1">
        <span className="text-neutral-500">Pending:</span>
        {pendingLabels.length === 0 ? (
          <span className="italic text-neutral-400">None</span>
        ) : (
          pendingLabels.map((item) => (
            <span
              key={item}
              className="rounded-full px-2 py-0.5 text-xs font-medium"
              style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}
            >
              {item}
            </span>
          ))
        )}
      </div>
    </div>
  );
};

export default PeersList;
